import React from "react";
import { IconX } from "@tabler/icons-react";
import { UseFormWatch } from "react-hook-form";
import { OrderFormData } from "../../../types/types";
import ButtonStyled from "../../../components/common/ButtonStyled";
import { useLanguage } from "../../../context/LanguageContext";

interface PreviewModalProps {
  onClose: () => void;
  onBack: () => void;
  onSubmit: React.MouseEventHandler<HTMLButtonElement>;
  watch: UseFormWatch<OrderFormData>;
  isSubmitting: boolean;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  onClose,
  onBack,
  onSubmit,
  watch,
  isSubmitting,
}) => {
  const { t } = useLanguage();
  const formData = watch();

  const formatDataForDisplay = (data: any) => {
    return Object.entries(data).reduce((acc, [key, value]) => {
      if (value === undefined || value === null || value === "") {
        return acc;
      }
      return {
        ...acc,
        [key]: value,
      };
    }, {});
  };

  const senderData = formatDataForDisplay({
    "select client": formData.clientName || "Not selected",
    name: formData.sender?.name,
    phone: {
      dialCode: formData.sender?.dialCode,
      mobile: formData.sender?.mobile,
    },
    email: formData.sender?.email,
    country: formData.sender?.countryName,
    city: formData.sender?.cityName,
    district: formData.sender?.districtName,
    "zip code": formData.sender?.zipCode,
    latitude: formData.sender?.latitude,
    longitude: formData.sender?.longitude,
    address: formData.sender?.address,
    "national address": formData.sender?.nationalAddress,
  });

  const receiverData = formatDataForDisplay({
    name: formData.receiver?.name,
    phone: {
      dialCode: formData.receiver?.dialCode,
      mobile: formData.receiver?.mobile,
    },
    email: formData.receiver?.email,
    country: formData.receiver?.countryName,
    city: formData.receiver?.cityName,
    district: formData.receiver?.districtName,
    "zip code": formData.receiver?.zipCode,
    latitude: formData.receiver?.latitude,
    longitude: formData.receiver?.longitude,
    address: formData.receiver?.address,
    "national address": formData.receiver?.nationalAddress,
    "street name": formData.receiver?.streetName,
    "building name": formData.receiver?.buildingName,
    "building number": formData.receiver?.buildingNumber,
  });

  const shipmentData = formatDataForDisplay({
    "payment mode": formData.shipment?.paymentType,
    "cod amount":
      formData.shipment?.paymentType === "COD"
        ? formData.shipment?.codAmount
        : undefined,
    "no. pieces": formData.shipment?.numberOfPieces,
    weight: formData.shipment?.totalWeight,
    "reference number": formData.shipment?.referenceNumber,
    "shipment type": formData.shipment?.shipmentType,
    service: formData.shipment?.serviceType,
    disc: formData.shipment?.description,
    "shipment cost": formData.shipment?.shipmentCost,
  });

  const PreviewSection = ({
    title,
    data,
  }: {
    title: string;
    data: Record<string, any>;
  }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border w-full">
      <h3 className="text-sm font-semibold text-gray-500 mb-4 uppercase">
        {t(title)}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        {Object.entries(data).map(([key, value]) => {
          const isPhone = key === "phone";
          return (
            <div key={key} className="w-full">
              <label className="block text-xs font-medium text-gray-600 mb-1 capitalize">
                {t(key)}
              </label>
              {isPhone ? (
                <div className="flex items-center border border-[#D8D8D8] rounded-full overflow-hidden w-full bg-white">
                  {typeof value === "object" && value !== null && value.dialCode && (
                    <span className="px-2 py-2 text-gray-700 text-sm bg-white">
                      +{value.dialCode}
                    </span>
                  )}
                  <input
                    disabled
                    value={
                      typeof value === "object" && value !== null
                        ? typeof value.mobile === "object" && value.mobile !== null
                          ? (value.mobile.mobile || JSON.stringify(value.mobile))
                          : (value.mobile || "")
                        : String(value || "")
                    }
                    className="flex-1 px-3 py-2 outline-none text-gray-800 text-sm bg-white border-l"
                  />
                </div>
              ) : (
                <input
                  disabled
                  className="bg-white w-full border border-[#D8D8D8] py-2 px-4 rounded-full text-sm"
                  value={
                    typeof value === "object" && value !== null
                      ? JSON.stringify(value)
                      : t(String(value || ""))
                  }
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const ShipmentField = ({
    label,
    value,
  }: {
    label: string;
    value: string;
  }) => (
    <div className="w-full">
      <label className="text-xs font-medium text-gray-600 mb-1 block capitalize">
        {t(label)}
      </label>
      <input
        disabled
        value={t(value)}
        className="bg-white w-full border border-[#D8D8D8] py-2 px-4 rounded-full text-sm"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-[#F7F6F2] rounded-2xl w-[1400px] max-h-[95vh] overflow-y-auto p-6 relative shadow-xl">
        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800">
            {t("Order Preview")}
          </h2>
          <button onClick={onClose}>
            <IconX />
          </button>
        </div>

        <div className="flex my-4 space-x-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="w-1/2 border-b-4 border-yellow-400 rounded-t-md"
            />
          ))}
        </div>

        <div className="grid grid-cols-2 gap-6">
          <PreviewSection title="Sender Information" data={senderData} />
          <PreviewSection title="Receiver Information" data={receiverData} />
        </div>

        <div className="mt-8">
          <h3 className="text-sm font-semibold text-gray-500 mb-4 uppercase">
            {t("Shipment Details")}
          </h3>
          <div className="grid grid-cols-4 gap-4">
            {Object.entries(shipmentData).map(([key, value]) => (
              <ShipmentField key={key} label={key} value={value as string} />
            ))}
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            onClick={onBack}
            className="px-5 py-2 text-sm rounded-full border border-gray-300 hover:bg-gray-100"
          >
            {t("Previous")}
          </button>

          <ButtonStyled
            value='Create'
            onClick={onSubmit}
            isPending={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;